// Member-profile permission checks — canAdmin / canSuperadmin / isSuperAdmin /
// canAccessPrivate / canEditProfile — and the page's view rules (the two
// views, their section rail, the Mail card gate; foot of the file). Ported from workspace/functions/
// roleHelpers.js (2026-08-15, MemberProfile extraction — tickets/opus.md,
// Nika's "extract MemberProfile into shared" ruling) so MemberProfile can
// live here and stay reachable from every host (workspace, canvas).
//
// Lives in SHARED (not workspace) for the exact reason appAccess.js's
// `isOwnerOrAdmin` does (same folder, same file header) — shared is
// consumed by every host and must not depend on the workspace package.
// This REPLICATES the claim-based legs of roleHelpers.js `hasPermission` /
// `canAdmin` / `canSuperadmin` / `canAccessPrivate` / `canEditProfile` /
// `isSuperAdmin` (root.jwtClaims / root.org.role / root.org.permissions —
// same primitives, no cross-package import) plus the legacy
// `root.roles.byUser` dual-read fallback via `findPerson` (already in this
// package, peopleUtils.js). The ONE branch intentionally NOT replicated is
// claimResolver.js's local team-permission UNION fallback (needs
// workspace's PERMISSION_KEYS / CATEGORY_PERMISSION_TEMPLATES catalog, a
// workspace-only import this file can't take) — same skip appAccess.js
// already made, same reasoning: `org.permissions` / `org.intranetPermissions`
// is populated by the same `/auth/me` call every claims-reading gate here
// already depends on, so the fallback gap is the same cold-load window
// every other claim gate tolerates.
//
// ONE OWNER PER NAME: these six names are registered by shared only. The shell
// registers its own resolvers prefixed (shellHasPermission, shellCanAdmin, …,
// workspace/functions/roleHelpers.js); each export below hands the call to the
// shell's when the calling element's bag has it, so the shell keeps its
// team-permission union and a catalog merge never picks between two
// definitions of one name.
//
// Every export below is a regular `function` (never an arrow) so
// `el.call('canAdmin', …)` / `el.call('isSuperAdmin')`'s `this` binding
// (smbls element/src/methods.js `method.call(this, ...)`) survives —
// same convention roleHelpers.js documents at its own header.
import { findPerson } from './peopleUtils.js'

// Permission-array resolution — mirrors appAccess.js `canApp`'s
// `permissionsFromRoot`, itself mirroring the PREFERRED (server-enriched)
// branches of workspace/functions/claimResolver.js `resolveClaims`.
const _permissionsFromRoot = (root) => {
  const org = root?.org
  if (org && Array.isArray(org.permissions)) return org.permissions
  if (org && Array.isArray(org.intranetPermissions)) return org.intranetPermissions
  return root?.jwtClaims?.permissions || []
}

// Owner bypass — mirrors roleHelpers.js `hasPermission`'s `_claims(r).is_owner`
// leg, sourced the same way appAccess.js `isOwnerOrAdmin` sources it.
const _isOwner = (root) => {
  const claims = root?.jwtClaims || {}
  if (claims.is_owner === true) return true
  return !!(root?.org && root.org.isOwner === true)
}

// True if the caller has the given permission key OR is an owner.
// el.call('hasPermission', perm) or hasPermission(root, perm).
export function hasPermission(root, perm) {
  // Inside the shell, the shell's resolver answers (it adds the team-permission
  // union this file cannot import); one owner per name, see the file header.
  const _host = this && this.context && this.context.functions && this.context.functions.shellHasPermission
  if (typeof _host === 'function') return _host.apply(this, arguments)
  const r = root != null ? root : this && this.state && this.state.root
  if (!r) return false
  if (_isOwner(r)) return true
  return _permissionsFromRoot(r).includes(perm)
}

const _currentPerson = (root) => (root ? findPerson(root, root.currentUserEmail) : null)

// Legacy `root.roles.byUser` lookup — the dual-read fallback leg every
// gate below keeps for accounts without JWT-carried permissions yet.
const _legacyHasRole = (person, root, role) => {
  if (!person) return false
  if (Array.isArray(person.roles) && person.roles.length > 0) return person.roles.includes(role)
  if (!root || !person.userId) return false
  return (root.roles?.byUser || {})[person.userId] === role
}
const _legacyIsAdmin = (person, root) =>
  _legacyHasRole(person, root, 'superadmin') || _legacyHasRole(person, root, 'admin')
const _legacyIsSuperadmin = (person, root) => _legacyHasRole(person, root, 'superadmin')

// Phase E — el.call('canAdmin', s) or canAdmin(s).
export function canAdmin(s) {
  // Inside the shell, the shell's resolver answers (it adds the team-permission
  // union this file cannot import); one owner per name, see the file header.
  const _host = this && this.context && this.context.functions && this.context.functions.shellCanAdmin
  if (typeof _host === 'function') return _host.apply(this, arguments)
  const root = s?.root || (this && this.state && this.state.root)
  return hasPermission(root, 'admin.pages.read') || _legacyIsAdmin(_currentPerson(root), root)
}

// el.call('canSuperadmin', s) or canSuperadmin(s).
export function canSuperadmin(s) {
  // Inside the shell, the shell's resolver answers (it adds the team-permission
  // union this file cannot import); one owner per name, see the file header.
  const _host = this && this.context && this.context.functions && this.context.functions.shellCanSuperadmin
  if (typeof _host === 'function') return _host.apply(this, arguments)
  const root = s?.root || (this && this.state && this.state.root)
  return (
    hasPermission(root, 'admin.danger.access') || _legacyIsSuperadmin(_currentPerson(root), root)
  )
}

// Cold-load-safe strict superadmin check. el.call('isSuperAdmin') — no args,
// root from this.state.root; or isSuperAdmin(s) with component state.
export function isSuperAdmin(s) {
  // Inside the shell, the shell's resolver answers (it adds the team-permission
  // union this file cannot import); one owner per name, see the file header.
  const _host = this && this.context && this.context.functions && this.context.functions.shellIsSuperAdmin
  if (typeof _host === 'function') return _host.apply(this, arguments)
  const root = (s && s.root) || (this && this.state && this.state.root)
  if (!root) return false
  const claims = root.jwtClaims || {}
  if (claims.symbols_role === 'admin' || claims.symbolsRole === 'admin') return true
  return _legacyIsSuperadmin(_currentPerson(root), root)
}

// Can view private data (equity, docs, salary) for a given person.
// Self OR `profile.private.read` perm OR legacy superadmin.
// el.call('canAccessPrivate', person) or canAccessPrivate(s, person).
export function canAccessPrivate(sOrPerson, maybePerson) {
  // Inside the shell, the shell's resolver answers (it adds the team-permission
  // union this file cannot import); one owner per name, see the file header.
  const _host = this && this.context && this.context.functions && this.context.functions.shellCanAccessPrivate
  if (typeof _host === 'function') return _host.apply(this, arguments)
  let root, person
  if (maybePerson !== undefined) {
    root = sOrPerson?.root || (this && this.state && this.state.root)
    person = maybePerson
  } else {
    root = this && this.state && this.state.root
    person = sOrPerson
  }
  if (!root) return true
  const isSelfUser = !person?.email || person.email === root.currentUserEmail
  if (isSelfUser) return true
  if (hasPermission(root, 'profile.private.read')) return true
  return _legacyIsSuperadmin(_currentPerson(root), root)
}

// Can edit a person's profile fields. Self OR `profile.edit_others` perm OR
// legacy superadmin. el.call('canEditProfile', person) or
// canEditProfile(s, person).
export function canEditProfile(sOrPerson, maybePerson) {
  // Inside the shell, the shell's resolver answers (it adds the team-permission
  // union this file cannot import); one owner per name, see the file header.
  const _host = this && this.context && this.context.functions && this.context.functions.shellCanEditProfile
  if (typeof _host === 'function') return _host.apply(this, arguments)
  let root, person
  if (maybePerson !== undefined) {
    root = sOrPerson?.root || (this && this.state && this.state.root)
    person = maybePerson
  } else {
    root = this && this.state && this.state.root
    person = sOrPerson
  }
  if (!root) return false
  const isSelfUser = !person?.email || person.email === root.currentUserEmail
  if (isSelfUser) return true
  if (hasPermission(root, 'profile.edit_others')) return true
  return _legacyIsSuperadmin(_currentPerson(root), root)
}

// Does the profile show the Availability card for this person? ONE rule for
// the card (MemberProfile ProfileAvailability.hide) and the section rails
// that list it, so a rail never names a hidden card. Everyone on the team
// has a schedule — the owner and co-owners too (the old allow-list of
// superadmin/admin/manager/member hid the owner's own card while the rail
// still listed it, live 2026-10-28). Guests and viewers sit outside the
// team; a person who left has none. el.call('showsAvailability', person) or
// showsAvailability(person, root).
export function showsAvailability(person, maybeRoot) {
  if (!person || !person.email) return false
  if (person.status === 'left') return false
  const outside = ['guest', 'viewer']
  const legacy = Array.isArray(person.roles) && person.roles.length > 0 ? person.roles : null
  if (legacy) return !legacy.every((r) => outside.includes(r))
  const root = maybeRoot || (this && this.state && this.state.root) || {}
  const byUser = (root.roles && root.roles.byUser) || {}
  const role = (person.userId && byUser[person.userId]) || 'member'
  return !outside.includes(role)
}

// ── The two views of the member profile (stage 3) ──
//
// Which half of the member profile a page shows (Nika 2026-10-28: "settings
// is workspace settings - but profiles and members should also have their
// own settings"). `/settings` (and its `/account` alias) is the person's OWN
// settings: accessibility, assistant, notifications, mail, security,
// sessions, danger zone. Every other route that mounts the page (/profile,
// /my, a member's `?u=` page) is the profile: the hero, details,
// availability, checklist, documents. Settings are personal, so another
// member's page is always the profile.
//
// The page's OWN route decides, not the window's: the avatar menu opens
// /settings in a PageSlot pane over any page, and the window keeps its own
// route there. The nearest pane stamp (canvas PageSlot) wins over
// `root.currentPath`:
//   • a keep-alive host carries its page's route (`_slotHref`), stamped before
//     the page mounts — every element of the page finds it first;
//   • the pane element carries only its key (`_slotPaneKey`); its route is
//     `root[key]`. PageSlot resolves a page's `metadata` with its OWN
//     elements (the pane title bar, the tab-title sync), which sit in the
//     pane but outside the host — measured live 2026-10-29: a /profile pane
//     over the /settings window titled itself "Settings".
// An idle pane (no route) reads as no pane. The window route is read on
// every run, so a factory that asks re-runs when it changes (FA515).
// el.call('memberProfileView', s) → 'settings' | 'profile'.
export function memberProfileView(state) {
  const el = this
  const ask = (name, ...args) =>
    el && typeof el.call === 'function' ? el.call(name, ...args) : undefined
  const root = (state && state.root) || (el && el.state && el.state.root) || null
  const live = root && typeof root.currentPath === 'string' ? root.currentPath : ''
  let route = null
  let node = el
  let hops = 0
  while (node && hops < 100) {
    const pane = node._slotPaneKey
    if (pane) {
      if (typeof node._slotHref === 'string') route = node._slotHref
      else if (root && typeof root[pane] === 'string' && root[pane]) route = root[pane]
      if (route != null) break
    }
    if (node.parent === node) break
    node = node.parent
    hops++
  }
  let path = String(route == null ? live : route)
    .split('?')[0]
    .split('#')[0]
  const stripped = ask('stripWorkspacePrefix', path)
  if (typeof stripped === 'string') path = stripped
  path = path.replace(/\/+$/, '') || '/'
  if (path !== '/settings' && path !== '/account') return 'profile'
  return ask('isSelf', state) === false ? 'profile' : 'settings'
}

// The section rail of the member profile pages (/settings, /profile, the
// shell's /my, canvas /settings): exactly the cards the page shows, in its
// reading order (the hero; the main stack, then the side stack). ONE list
// for every host page, so a rail row never names a hidden card and a shown
// card always has a row: the /my rail listed a hidden card and missed two
// shown ones (live 2026-10-28). Each gate is the card's own rule.
//
// `sectionKey` is the SCROLL KEY: the card's `data-content-section`, an
// opaque English key that never goes through polyglot (a translated key
// breaks the scroll on the first language switch). `label` is the visible
// word in the FUNCTION form, so the rail follows a switch.
// el.call('memberProfileSections', s).
export function memberProfileSections(state) {
  const el = this
  const root = (state && state.root) || (el && el.state && el.state.root) || null
  const row = (sectionKey, key) => ({ sectionKey, label: el.call('polyglot', key) })
  const list = [row('Profile', 'account.profile.sections.profile')]
  if (el.call('memberProfileView', state) === 'settings') {
    list.push(row('Accessibility', 'account.profile.sections.accessibility'))
    // The Assistant card shows wherever the shell's writer is present.
    const fns = el.context && el.context.functions
    if (fns && typeof fns.setAssistantAnswersMode === 'function') {
      list.push(row('Assistant', 'account.profile.sections.assistant'))
    }
    list.push(row('Notifications', 'account.profile.sections.notifications'))
    if (el.call('showsMailSettings', state)) {
      list.push(row('Mail', 'account.profile.sections.mail'))
    }
    list.push(
      row('Security', 'account.profile.sections.security'),
      row('Sessions', 'account.profile.sections.sessions'),
      row('Danger zone', 'account.profile.sections.danger_zone')
    )
    return list
  }
  const person = el.call('findMe', state) || {}
  list.push(row('Details', 'account.profile.sections.details'))
  if (el.call('showsAvailability', person, root)) {
    list.push(row('Availability', 'account.profile.sections.availability'))
  }
  if (el.call('canAccessPrivate', person)) {
    list.push(
      row('Checklist', 'account.profile.sections.checklist'),
      row('Documents', 'account.profile.sections.documents')
    )
  }
  if (!el.call('isSelf', state) && el.call('canSuperadmin', state)) {
    list.push(row('Member access', 'account.profile.sections.access'))
  }
  return list
}

// Does the settings page show the Mail card (architecture/MAIL.md §3.4)? ONE
// gate for the card's mount (shared/pages/profile.js) and its rail row, in
// the order the three can fail:
//   1. the component is REGISTERED: canvas links no mail package, and an
//      unresolved bare key would still mount an empty box;
//   2. it is the person's OWN page: a personal preference;
//   3. the `mail` feature flag is on for this workspace: `navItemVisible`, the
//      one route→flag resolver every navigation surface uses.
// el.call('showsMailSettings', s).
export function showsMailSettings(state) {
  const el = this
  const comps = el && el.context && el.context.components
  if (!comps || !comps.MailAccountSettingsCard) return false
  if (!el.call('isSelf', state)) return false
  const root = (state && state.root) || (el.state && el.state.root) || null
  return !!el.call('navItemVisible', root, '/mail')
}
